import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { autoTrackLinkUrl } from '../../config/autoTrackLinks';
import { trackEvent } from '../../../analytics/sml/analytics';

interface CopyTrackLinkButtonProps {
  linkSlug: string | null | undefined;
}

const CONFIRMATION_MS = 2000;

const CopyTrackLinkButtonComponent: React.FC<CopyTrackLinkButtonProps> = ({ linkSlug }) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current !== null) {
        clearTimeout(resetTimer.current);
      }
    },
    []
  );

  const onCopy = useCallback(() => {
    if (linkSlug === null || linkSlug === undefined) {
      return;
    }
    navigator.clipboard
      .writeText(autoTrackLinkUrl(linkSlug))
      .then(() => {
        setCopied(true);
        trackEvent('link_copied', { kind: 'track' });
        resetTimer.current = setTimeout(() => setCopied(false), CONFIRMATION_MS);
      })
      .catch(console.error);
  }, [linkSlug]);

  // A track the slug backfill has not reached yet has no link to copy.
  if (linkSlug === null || linkSlug === undefined) {
    return null;
  }

  return (
    <Button
      color="outline-secondary"
      size="sm"
      className="rounded-pill px-3 d-inline-flex align-items-center gap-2"
      onClick={onCopy}
    >
      <FontAwesomeIcon icon={copied ? faCheck : faLink} />
      <span className="fw-medium">
        {copied
          ? intl.formatMessage({ id: 'app.sml.track.details.copyLinkDone', defaultMessage: 'Copied' })
          : intl.formatMessage({ id: 'app.sml.track.details.copyLink', defaultMessage: 'Copy SmartLink' })}
      </span>
    </Button>
  );
};

export const CopyTrackLinkButton = TypedMemo(CopyTrackLinkButtonComponent);
